/**
 * Meta Pixel (not injected globally in index.html). Same pixel id powers job funnels
 * and auth surfaces (signup).
 *
 * Track every job separately: set REACT_APP_META_PIXEL_ID and omit the job filters
 * below. PageView / Lead include content_ids so Meta can segment by listing.
 *
 *   REACT_APP_META_PIXEL_ID=your_pixel_id   (required for any tracking)
 *
 * Optional — restrict to specific listings only:
 *   REACT_APP_META_TRACKING_JOB_ID=single_slug_or_id
 *   REACT_APP_META_TRACKING_JOB_IDS=comma,separated,ids
 * Omit both when you want every job tracked automatically.
 */

const SCRIPT_ATTR = "data-arno-meta-pixel-job";

function normalizeId(id: string | number | undefined | null): string {
  if (id === undefined || id === null) return "";
  return String(id).trim();
}

function getPixelId(): string {
  return normalizeId(process.env.REACT_APP_META_PIXEL_ID);
}

/**
 * Whitelist of job route ids/slugs to track. `null` = no filter (all jobs).
 */
function getJobIdWhitelist(): Set<string> | null {
  const csv = normalizeId(process.env.REACT_APP_META_TRACKING_JOB_IDS);
  const parts = csv
    ? csv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  if (parts.length > 0) {
    return new Set(parts);
  }
  const single = normalizeId(process.env.REACT_APP_META_TRACKING_JOB_ID);
  if (single) {
    return new Set([single]);
  }
  return null;
}

export function isMetaJobTrackingConfigured(): boolean {
  return Boolean(getPixelId());
}

/** True when this route id/slug should receive PageView / Lead (or all jobs if no whitelist). */
export function isTrackedJobId(id: string | number | undefined | null): boolean {
  if (!isMetaJobTrackingConfigured()) return false;
  const n = normalizeId(id);
  if (!n) return false;
  const allow = getJobIdWhitelist();
  if (allow === null) return true;
  return allow.has(n);
}

let initPromise: Promise<void> | null = null;
let initialized = false;

function runFbq(
  command: "init" | "track",
  arg: string,
  payload?: Record<string, unknown>,
): void {
  const w = window as Window & { fbq?: (...args: unknown[]) => void };
  if (typeof w.fbq !== "function") return;
  if (payload !== undefined) {
    w.fbq(command, arg, payload);
  } else {
    w.fbq(command, arg);
  }
}

function injectMetaPixelBase(): void {
  if (document.querySelector(`script[${SCRIPT_ATTR}]`)) {
    return;
  }
  const inline = document.createElement("script");
  inline.setAttribute(SCRIPT_ATTR, "1");
  inline.textContent = `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');`;
  document.head.appendChild(inline);
}

/**
 * Loads the Meta Pixel base (once) and initializes with the configured pixel id.
 */
export function ensureMetaPixelForTrackedJob(): Promise<void> {
  if (!isMetaJobTrackingConfigured()) {
    return Promise.resolve();
  }

  if (initialized) {
    return Promise.resolve();
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = new Promise<void>((resolve) => {
    injectMetaPixelBase();
    const pixelId = getPixelId();
    const tryInit = (): void => {
      const w = window as Window & { fbq?: (...args: unknown[]) => void };
      if (typeof w.fbq === "function" && !initialized) {
        runFbq("init", pixelId);
        initialized = true;
      }
    };
    // Inline script should run synchronously; microtask + short timeout cover edge cases on first paint.
    queueMicrotask(() => {
      tryInit();
      if (!initialized) {
        setTimeout(() => {
          tryInit();
          resolve();
        }, 50);
      } else {
        resolve();
      }
    });
  });

  return initPromise;
}

/** PageView on a job detail route — includes content_ids when possible for per-job breakdown. */
export function trackMetaJobPageView(jobId?: string | number): void {
  if (!isMetaJobTrackingConfigured()) return;
  const n = normalizeId(jobId);
  if (!n || !isTrackedJobId(n)) return;
  ensureMetaPixelForTrackedJob().then(() => {
    runFbq("track", "PageView", {
      content_ids: [n],
      content_type: "job_listing",
    });
  });
}

/** Lead — completed application for a job (successful like/apply). */
export function trackMetaJobLead(
  jobId: string | number,
  extra?: { content_name?: string },
): void {
  if (!isTrackedJobId(jobId)) return;
  ensureMetaPixelForTrackedJob().then(() => {
    runFbq("track", "Lead", {
      content_ids: [normalizeId(jobId)],
      ...extra,
    });
  });
}

function listingMatchesScope(job: {
  id?: string | number;
  slug?: string | null;
}): boolean {
  const allow = getJobIdWhitelist();
  if (allow === null) return true;
  const idStr = normalizeId(job.id);
  const slugStr = job.slug != null ? normalizeId(job.slug) : "";
  if (idStr && allow.has(idStr)) return true;
  if (slugStr && allow.has(slugStr)) return true;
  return false;
}

/** Prefer when you have a listing: sends stable content_ids + optional title. */
export function trackMetaJobLeadForListing(job: {
  id?: string | number;
  slug?: string | null;
  title?: string;
}): void {
  if (!isMetaJobTrackingConfigured()) return;
  if (!listingMatchesScope(job)) return;
  const idStr = normalizeId(job.id);
  const slugStr = job.slug != null ? normalizeId(job.slug) : "";
  if (!idStr && !slugStr) return;
  ensureMetaPixelForTrackedJob().then(() => {
    const contentId = slugStr || idStr;
    runFbq("track", "Lead", {
      content_ids: [contentId],
      content_type: "job_listing",
      ...(job.title ? { content_name: job.title } : {}),
    });
  });
}

/** `/jobs` list — user tapped Apply (intent; Lead still fires on successful apply). */
export function trackMetaJobsListApplyClick(job: {
  id?: string | number;
  slug?: string | null;
  title?: string;
}): void {
  if (!isMetaJobTrackingConfigured()) return;
  if (!listingMatchesScope(job)) return;
  const idStr = normalizeId(job.id);
  const slugStr = job.slug != null ? normalizeId(job.slug) : "";
  if (!idStr && !slugStr) return;
  ensureMetaPixelForTrackedJob().then(() => {
    const contentId = slugStr || idStr;
    runFbq("track", "SubmitApplication", {
      content_ids: [contentId],
      content_type: "job_listing",
      ...(job.title ? { content_name: job.title } : {}),
    });
  });
}

/** `/jobs` list — user tapped View Details (navigates to job detail). */
export function trackMetaJobsListViewDetailsClick(job: {
  id?: string | number;
  slug?: string | null;
  title?: string;
}): void {
  if (!isMetaJobTrackingConfigured()) return;
  if (!listingMatchesScope(job)) return;
  const idStr = normalizeId(job.id);
  const slugStr = job.slug != null ? normalizeId(job.slug) : "";
  if (!idStr && !slugStr) return;
  ensureMetaPixelForTrackedJob().then(() => {
    const contentId = slugStr || idStr;
    runFbq("track", "ViewContent", {
      content_ids: [contentId],
      content_type: "job_listing",
      ...(job.title ? { content_name: job.title } : {}),
    });
  });
}

/**
 * Talent `/jobs` listing (not a single job URL). Sends PageView so Meta shows activity;
 * per-job detail pages use {@link trackMetaJobPageView} with content_ids.
 */
export function trackMetaJobsDirectoryPageView(): void {
  if (!isMetaJobTrackingConfigured()) return;
  ensureMetaPixelForTrackedJob().then(() => {
    runFbq("track", "PageView", {
      content_category: "jobs_directory",
      content_type: "job_listing",
    });
  });
}

/** `/sign-up` — page load (role picker or email form). */
export function trackMetaSignupPageView(): void {
  if (!isMetaJobTrackingConfigured()) return;
  ensureMetaPixelForTrackedJob().then(() => {
    runFbq("track", "PageView", {
      content_category: "signup",
    });
  });
}

/** Email/password registration succeeded (after API success). */
export function trackMetaSignupComplete(options?: {
  /** e.g. talent, recruiter, is_corporate */
  content_name?: string;
}): void {
  if (!isMetaJobTrackingConfigured()) return;
  ensureMetaPixelForTrackedJob().then(() => {
    const payload: Record<string, unknown> = {};
    if (options?.content_name) {
      payload.content_name = options.content_name;
    }
    runFbq(
      "track",
      "CompleteRegistration",
      Object.keys(payload).length ? payload : undefined,
    );
  });
}

/** User chose Google/LinkedIn signup (before redirect to provider). */
export function trackMetaSignupOAuthClick(
  provider: "google" | "linkedin",
  accountType?: string,
): void {
  if (!isMetaJobTrackingConfigured()) return;
  ensureMetaPixelForTrackedJob().then(() => {
    const w = window as Window & { fbq?: (...args: unknown[]) => void };
    if (typeof w.fbq !== "function") return;
    w.fbq("trackCustom", "SignUpOAuthClick", {
      provider,
      ...(accountType ? { content_name: accountType } : {}),
    });
  });
}
